/**
 * Global trading mode (paper ⇄ real) — the spine of the connection story. Kept in its OWN leaf
 * module (no queries/omauth imports) so the order client (orderClient.ts) and omauth can read/set
 * it without pulling in the data layer, which would create an import cycle.
 *
 * NOTE: whether REAL is actually permitted also depends on the trade scope (omauth.canTrade) —
 * the order client (armed only for Real + scope) AND the top-bar switch enforce that; this
 * store just holds the preference.
 */
import { useSyncExternalStore } from "react";

export type TradingMode = "paper" | "real";
const MODE_KEY = "gb.trading-mode";

function readMode(): TradingMode {
  try {
    return localStorage.getItem(MODE_KEY) === "real" ? "real" : "paper";
  } catch {
    return "paper";
  }
}

let mode: TradingMode = readMode();
const listeners = new Set<() => void>();

export function getTradingMode(): TradingMode {
  return mode;
}
export function setTradingMode(next: TradingMode): void {
  mode = next;
  try {
    localStorage.setItem(MODE_KEY, next);
  } catch {
    /* ignore */
  }
  for (const l of listeners) l();
}
export const tradingModeStore = {
  subscribe(fn: () => void): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
  getSnapshot(): TradingMode {
    return mode;
  },
};
export function useTradingMode(): TradingMode {
  return useSyncExternalStore(tradingModeStore.subscribe, tradingModeStore.getSnapshot, () => "paper");
}
